import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { isMobile } from "react-device-detect";

interface SidebarNavigationContextValue {
  /**
   * Estado de expansão do menu lateral.
   *
   * Importante:
   * - Este estado deve ficar FORA do `ViewContext` para evitar rerenderizações
   *   globais quando o usuário abre/fecha o colapse.
   * - Deve ser consumido apenas por layout/header/sidebar e pontos que
   *   fecham o menu ao navegar.
   */
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: (expanded: boolean) => void;
  toggleSidebarExpanded: () => void;
}

const SidebarNavigationContext =
  createContext<SidebarNavigationContextValue>(null!);

export function SidebarNavigationProvider({ children }: { children: ReactNode }) {
  const [isSidebarExpanded, setIsSidebarExpandedState] = useState<boolean>(
    isMobile ? false : true,
  );

  const setIsSidebarExpanded = useCallback((expanded: boolean) => {
    setIsSidebarExpandedState(expanded);
  }, []);

  const toggleSidebarExpanded = useCallback(() => {
    setIsSidebarExpandedState((prev) => !prev);
  }, []);

  const value = useMemo<SidebarNavigationContextValue>(
    () => ({
      isSidebarExpanded,
      setIsSidebarExpanded,
      toggleSidebarExpanded,
    }),
    [isSidebarExpanded, setIsSidebarExpanded, toggleSidebarExpanded],
  );

  return (
    <SidebarNavigationContext.Provider value={value}>
      {children}
    </SidebarNavigationContext.Provider>
  );
}

export function useSidebarNavigation() {
  return useContext(SidebarNavigationContext);
}

